import { useCallback, useMemo, useRef, useState } from 'react';
import type { LatLng } from '@/apis/types';

export interface PolygonEditSession {
  polygon: LatLng[];
  isClosed: boolean;
  resetTrigger: number;
  setPolygon: (polygon: LatLng[]) => void;
  setIsClosed: (closed: boolean) => void;
  undo: () => void;
  load: (polygon: LatLng[], closed: boolean) => void;
  discard: () => void;
  isValid: boolean;
  isEmpty: boolean;
}

// Shared polygon/closed/resetTrigger state for the three geo-fence editors.
// `load` seeds the editable state and records it as the snapshot `discard`
// reverts to.
const usePolygonEditSession = (): PolygonEditSession => {
  const [polygon, setPolygon] = useState<LatLng[]>([]);
  const [isClosed, setIsClosed] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const snapshotRef = useRef<{ polygon: LatLng[]; closed: boolean }>({
    polygon: [],
    closed: false,
  });

  const load = useCallback((nextPolygon: LatLng[], nextClosed: boolean) => {
    snapshotRef.current = { polygon: nextPolygon, closed: nextClosed };
    setPolygon(nextPolygon);
    setIsClosed(nextClosed);
    setResetTrigger((current) => current + 1);
  }, []);

  const undo = useCallback(() => {
    setPolygon((current) => current.slice(0, -1));
    setIsClosed(false);
    setResetTrigger((current) => current + 1);
  }, []);

  const discard = useCallback(() => {
    load(snapshotRef.current.polygon, snapshotRef.current.closed);
  }, [load]);

  // Memoized so consumers can depend on the returned object itself (e.g. in
  // a useEffect) without exhaustive-deps issues or extra re-renders.
  return useMemo(
    () => ({
      polygon,
      isClosed,
      resetTrigger,
      setPolygon,
      setIsClosed,
      undo,
      load,
      discard,
      isValid: isClosed && polygon.length >= 3,
      isEmpty: polygon.length === 0,
    }),
    [polygon, isClosed, resetTrigger, undo, load, discard]
  );
};

export default usePolygonEditSession;
